import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React from 'react';
import type { InvitationRead } from '../../../../../js/api/models/InvitationRead';

const CardHeading: React.FC<React.ComponentPropsWithoutRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      {...props}
      className={clsx(className, 'tw:!text-[16px] tw:font-semibold tw:text-zinc-900 tw:!mb-0')}
    >
      {children}
    </p>
  );
};

export const PendingInvitationCard: React.FC<{
  invitation: InvitationRead;
  roleLabelMap?: Record<string, string>;
  isSelected?: boolean;
  showOrganization?: boolean;
  actions?: React.ReactNode;
}> = ({ invitation, roleLabelMap = {}, isSelected = false, showOrganization = false, actions }) => {
  const invitedOn = invitation.created_at
    ? format(parseISO(invitation.created_at), 'LLL d, yyyy')
    : '—';
  const roleLabel = invitation.role ? roleLabelMap[invitation.role] || invitation.role : null;

  return (
    <div
      data-testid={`pending-invitation-card-${invitation.id}`}
      className={clsx(
        'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow',
        isSelected && 'tw:!bg-brand-green-0/10 tw:!shadow-md'
      )}
    >
      <div className="tw:p-3 tw:flex tw:flex-col tw:h-full tw:min-h-0">
        <div className="tw:flex-1 tw:min-h-0">
          <div className="tw:flex tw:justify-between tw:items-start tw:mb-2">
            <CardHeading className="tw:line-clamp-2">
              <i className="far fa-fw fa-lg fa-envelope tw:mr-1" />
              {invitation.email}
            </CardHeading>
            <span className="tw:block tw:mt-2 tw:text-xs tw:text-zinc-500">
              Invited on {invitedOn}
            </span>
          </div>
          {showOrganization && invitation.healthcare_entity_name && (
            <div
              data-testid={`pending-invitation-card-org-${invitation.id}`}
              className="tw:text-xs tw:text-zinc-500 tw:mb-1"
            >
              {invitation.healthcare_entity_name}
            </div>
          )}
          {roleLabel && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1 tw:text-sm tw:text-zinc-600">
              <i className="far fa-fw fa-user-tag tw:text-zinc-400" />
              <span>{roleLabel}</span>
            </div>
          )}
        </div>
        {actions && (
          <div
            className="tw:mt-auto tw:flex tw:gap-1"
            onClick={(e) => e.stopPropagation()}
            role="button"
            tabIndex={0}
            onKeyUp={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingInvitationCard;
